import { isBillRecurringFrequency, RECURRING_MAX_ALLOWED_OCCURRENCES } from '@melio/ap-domain';
import { useMelioIntl } from '@melio/platform-i18n';
import { addYears, isAfter, isBefore } from 'date-fns';
import { pickBy } from 'lodash';
import { BaseSchema, date, number, object, SchemaOf, string } from 'yup';

import { AddInstantBillV2FormActivityFields } from './types';

export const useAddInstantBillV2FormSchema = (fieldsForValidation: string[]) => {
  const { formatMessage, formatCurrency, formatDate } = useMelioIntl();

  const schema = {
    vendorId: string()
      .required(formatMessage('activities.addInstantBillV2Form.vendorName.validation.required'))
      .nullable(),
    billAmount: string()
      .required(formatMessage('activities.addInstantBillV2Form.billAmount.validation.required'))
      .test(
        'greaterThan',
        formatMessage('activities.addInstantBillV2Form.billAmount.validation.greaterThan', {
          minValue: formatCurrency(0),
        }),
        (value = '') => parseFloat(value) > 0
      ),
    paymentFrequency: string().required(),
    startDate: date()
      .nullable()
      .required(formatMessage('activities.addInstantBillV2Form.startDate.validation.required')),
    endPolicy: string().required(),
    numOfOccurrences: number()
      .positive(formatMessage('activities.addInstantBillV2Form.numOfOccurrences.validation.valid'))
      .integer()
      .nullable()
      .required(formatMessage('activities.addInstantBillV2Form.numOfOccurrences.validation.required'))
      .typeError(formatMessage('activities.addInstantBillV2Form.numOfOccurrences.validation.required'))
      .when('paymentFrequency', (paymentFrequency: string, schema: BaseSchema) =>
        schema.test(
          'is-number-allowed',
          isBillRecurringFrequency(paymentFrequency)
            ? formatMessage('activities.addInstantBillV2Form.numOfOccurrences.validation.maxLimit', {
                maxNumOfPayments: RECURRING_MAX_ALLOWED_OCCURRENCES[paymentFrequency],
              })
            : '',
          (numOfOccurrences: number) =>
            !(
              isBillRecurringFrequency(paymentFrequency) &&
              numOfOccurrences &&
              numOfOccurrences > RECURRING_MAX_ALLOWED_OCCURRENCES[paymentFrequency]
            )
        )
      ),
    endDate: date()
      .nullable()
      .required(formatMessage('activities.addInstantBillV2Form.endDate.validation.required'))
      .when('startDate', (startDate: Date, schema: BaseSchema) =>
        schema
          .test(
            'is-date-valid',
            formatMessage('activities.addInstantBillV2Form.endDate.validation.validDate'),
            (endDate: Date) => !(startDate && endDate && isBefore(endDate, startDate))
          )
          .test(
            'is-exceeding-max-payments',
            startDate
              ? formatMessage('activities.addInstantBillV2Form.endDate.validation.maxLimit', {
                  maxDate: formatDate(addYears(startDate, 2), { dateStyle: 'medium' }),
                })
              : '',
            (endDate: Date) => !(startDate && endDate && isAfter(endDate, addYears(startDate, 2)))
          )
      ),
    categoryId: string().optional(),
  };

  return object().shape(
    pickBy(schema, (_, key: string) => fieldsForValidation.includes(key))
  ) as unknown as SchemaOf<AddInstantBillV2FormActivityFields>;
};
